import React, { useEffect, useState } from 'react';
import { DollarSign, ArrowUpRight } from 'lucide-react';
import { connect } from 'react-redux';
import { useAssistantMode } from '../../hooks/useAssistantMode';
import { RootState } from '../../store';
import { OwnedBetsState } from '../../store/ownedBets/reducer';
import { DashboardState } from '../../store/dashboard/reducer';
import axios from 'axios';
import { formatNumber } from '../Dashboard/Utils/dashboardUtils';
import { MyTipsTipBox } from '../MyTips/MyTipsTipBox';
import { get } from 'lodash';
import { Money } from '../../utils/money/Money';
import { useTranslation } from 'react-i18next';
import { tr } from '../../utils/translations/translator';

// Helper para formatear dinero con currency code pequeño
const FormatMoneySmall: React.FC<{ money: Money | null | undefined }> = ({ money }) => {
  if (!money) return <>€0,00</>;
  const formatted = money.toString();
  const parts = formatted.split(' ');
  if (parts.length === 2) {
    return (
      <>
        {parts[0]} <span className="text-xs">{parts[1]}</span>
      </>
    );
  }
  return <>{formatted}</>;
};

interface StateProps {
  ownedBets: OwnedBetsState;
  dashboard: DashboardState;
  user: any;
}

interface DispatchProps {}

type TipsterDashboardProps = StateProps & DispatchProps;

const TipsterDashboard: React.FC<TipsterDashboardProps> = ({
  dashboard,
  user,
  ownedBets,
}) => {
  const { t } = useTranslation();
  const { isAssistantMode } = useAssistantMode();
  const [historicSales, setHistoricSales] = useState<Money | null>(null);
  const [monthSales, setMonthSales] = useState<Money | null>(null);
  const [withdrawalBalance, setWithdrawalBalance] = useState<Money | null>(
    null,
  );
  const [pendingSales, setPendingSales] = useState<Money | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setIsLoading(true);

        // Fetch historic sales
        const historicResponse = await axios.get('/v1/me/sales', {
          params: {
            period_filter: 'HISTORIC',
            income_source: 'ALL',
            limit: 50,
          },
        });

        // Fetch current month sales
        const monthResponse = await axios.get('/v1/me/sales', {
          params: {
            period_filter: 'CURRENT_MONTH',
            income_source: 'ALL',
            limit: 50,
          },
        });

        // Fetch balance
        const balanceResponse = await axios.get('/v1/me/balance', {
          params: {
            limit: 50,
          },
        });

        // Get amount and decimals from API response
        // Check if data is directly in response or in response.data
        const historicResponseData = historicResponse.data || historicResponse;
        const monthResponseData = monthResponse.data || monthResponse;

        const historicData =
          historicResponseData?.sales_data?.summary?.total_sales;
        const monthData = monthResponseData?.sales_data?.summary?.total_sales;

        // Use Money class for proper currency formatting with override support
        const historicFormatted = historicData ? new Money(historicData) : null;
        const monthFormatted = monthData ? new Money(monthData) : null;

        // Get balance data
        const balanceResponseData = balanceResponse.data || balanceResponse;
        const balanceData =
          balanceResponseData?.balance?.summary?.available_withdrawal;
        const pendingSalesData =
          balanceResponseData?.balance?.summary?.pending_sales;

        // Format balance with Money class
        const balanceFormatted = balanceData ? new Money(balanceData) : null;

        // Format pending sales with Money class
        const pendingSalesFormatted = pendingSalesData
          ? new Money(pendingSalesData)
          : null;

        // Store as Money objects
        setHistoricSales(historicFormatted);
        setMonthSales(monthFormatted);
        setWithdrawalBalance(balanceFormatted);
        setPendingSales(pendingSalesFormatted);
      } catch (error) {
        console.error('Error fetching sales data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalesData();
  }, []);

  // Get open tips from ownedBets
  const openTips = ownedBets.data.open.tips || [];
  const openMatches = ownedBets.data.open.matches || [];
  const oddsFormat = get(user, ['odds_format']) || 'DECIMAL';

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 pb-24">
      <div className="max-w-7xl mx-auto w-full">
        {/* Header with gradient */}
        <div className="mb-8 bg-gradient-to-bl from-[#6633ff] to-[#121619] rounded-xl p-6 md:p-8 shadow-lg overflow-visible">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {tr(t, 'hello')}, {user?.user?.username || 'Tipster'} 👋
          </h1>
          <p className="text-white/80 text-sm md:text-base">
            {tr(t, 'welcome_back')}
          </p>
        </div>

        {/* Stats Cards */}
        {!isAssistantMode && <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sales Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                {tr(t, 'current_month_sales')}
              </h3>
              <DollarSign
                className="w-5 h-5 text-[#6633ff]"
                strokeWidth={2}
                fill="none"
              />
            </div>
            {isLoading ? (
              <div className="space-y-3">
                <p className="text-4xl font-bold text-gray-400">...</p>
                <p className="text-sm text-gray-400">...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-4xl font-bold text-[#242424]">
                  <FormatMoneySmall money={monthSales} />
                </p>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">{tr(t, 'in_total')}</p>
                  <p className="text-lg font-semibold text-[#6633ff]">
                    <FormatMoneySmall money={historicSales} />
                  </p>
                </div>
              </div>
            )}
            <a
              href="/sales"
              className="mt-4 inline-flex items-center text-sm text-[#6633ff] font-medium hover:underline"
            >
              {tr(t, 'view_details')}
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </a>
          </div>

          {/* Available Balance Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                {tr(t, 'available_balance')}
              </h3>
              <DollarSign
                className="w-5 h-5 text-[#6633ff]"
                strokeWidth={2}
                fill="none"
              />
            </div>
            {isLoading ? (
              <div className="space-y-3">
                <p className="text-4xl font-bold text-gray-400">...</p>
                <p className="text-sm text-gray-400">...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-4xl font-bold text-[#242424]">
                  <FormatMoneySmall money={withdrawalBalance} />
                </p>
                {/* <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">
                    Ventas pendientes
                  </p>
                  <p className="text-lg font-semibold text-yellow-600">
                    €{pendingSales}
                  </p>
                </div> */}
              </div>
            )}
            <a
              href="/balance"
              className="mt-4 inline-flex items-center text-sm text-[#6633ff] font-medium hover:underline"
            >
              {tr(t, 'withdraw')}
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>}

        {/* Open Tips Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#242424]">{tr(t, 'open_tips')}</h2>
            <a
              href="/my-bets"
              className="text-[#6633ff] text-sm font-medium hover:underline flex items-center gap-1"
            >
              {tr(t, 'view_all')}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {openTips.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {openTips.slice(0, 6).map((tip: any) => (
                <MyTipsTipBox
                  key={tip.id}
                  tip={tip}
                  oddsFormat={oddsFormat}
                  allMatches={openMatches}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign
                  className="w-8 h-8 text-gray-400"
                  strokeWidth={2}
                  fill="none"
                />
              </div>
              <h3 className="text-lg font-semibold text-[#242424] mb-2">
                {tr(t, 'no_open_tips_dashboard')}
              </h3>
              <a
                href="/pc"
                className="inline-flex items-center px-4 py-2 bg-[#6633ff] text-white rounded-lg font-medium hover:bg-[#5522ee] transition-colors"
              >
                {tr(t, 'publish_tip')}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState): StateProps => ({
  ownedBets: state.ownedBets,
  dashboard: state.dashboard,
  user: state.login.user,
});

const mapDispatchToProps = (): DispatchProps => ({});

export const TipsterDashboardConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TipsterDashboard);
